import { Box, Card, CardContent, Grid, Stack, Tab, Typography, useTheme } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { DateCalendar, DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useTranslation } from 'react-i18next';
import ScreenDataUsageStatsChart from './ScreenDataUsageStatsChart';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const toNumber = (value) => {
  if (value == null) {
    return 0;
  }

  if (typeof value === 'object' && value.low != null) {
    const lowValue = Number(value.low);
    return Number.isFinite(lowValue) ? lowValue : 0;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const toDateKey = (dateValue) => {
  if (!dateValue) {
    return null;
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString('en-CA', { timeZone: 'UTC' });
};

const formatDateLabel = (dateKey) => {
  if (!dateKey) {
    return '';
  }

  return new Date(`${dateKey}T00:00:00.000Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const shiftDateKey = (dateKey, days) => {
  if (!dateKey) {
    return null;
  }

  const shiftedDate = new Date(`${dateKey}T00:00:00.000Z`);

  if (Number.isNaN(shiftedDate.getTime())) {
    return null;
  }

  shiftedDate.setUTCDate(shiftedDate.getUTCDate() - days);
  return shiftedDate.toISOString().split('T')[0];
};

const diffDaysFromLatest = (latestDateKey, targetDateKey) => {
  if (!latestDateKey || !targetDateKey) {
    return null;
  }

  const latestDate = new Date(`${latestDateKey}T00:00:00.000Z`);
  const targetDate = new Date(`${targetDateKey}T00:00:00.000Z`);

  if (Number.isNaN(latestDate.getTime()) || Number.isNaN(targetDate.getTime())) {
    return null;
  }

  return Math.round((latestDate - targetDate) / DAY_IN_MS);
};

const StatCard = ({ icon, title, value, subtitle, accentColor, isDarkTheme }) => {
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderColor: isDarkTheme ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
        backgroundColor: isDarkTheme ? 'rgba(255,255,255,0.03)' : '#fafafa',
      }}
    >
      <CardContent>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isDarkTheme ? 'rgba(255,255,255,0.06)' : `${accentColor}14`,
              color: accentColor,
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" sx={{ lineHeight: 1.2 }}>
              {title}
            </Typography>
            <Typography variant="h4" sx={{ wordBreak: 'break-word' }}>
              {value}
            </Typography>
            {subtitle ? (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

const ScreenInternetUsage = ({ screen }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDarkTheme = theme.palette.mode === 'dark';
  const [currentTab, setCurrentTab] = useState('per-day');

  const downloadedBytesRecords = useMemo(
    () => (Array.isArray(screen?.downloadedBytesRecords) ? screen.downloadedBytesRecords : []),
    [screen?.downloadedBytesRecords],
  );

  const sentBytesRecords = useMemo(
    () => (Array.isArray(screen?.sentBytesRecords) ? screen.sentBytesRecords : []),
    [screen?.sentBytesRecords],
  );

  const latestDateKey = useMemo(() => toDateKey(screen?.lastInternetRecord), [screen?.lastInternetRecord]);
  const oldestAvailableDateKey = useMemo(() => {
    if (!latestDateKey) {
      return null;
    }

    const availableDays = Math.max(downloadedBytesRecords.length, sentBytesRecords.length) - 1;
    if (availableDays < 0) {
      return latestDateKey;
    }

    return shiftDateKey(latestDateKey, availableDays);
  }, [downloadedBytesRecords.length, latestDateKey, sentBytesRecords.length]);

  const initialSelectedDay = latestDateKey ?? toDateKey(new Date());
  const [selectedDayKey, setSelectedDayKey] = useState(initialSelectedDay);
  const [rangeFromKey, setRangeFromKey] = useState(initialSelectedDay);
  const [rangeToKey, setRangeToKey] = useState(initialSelectedDay);

  useEffect(() => {
    const fallbackDay = latestDateKey ?? toDateKey(new Date());
    const availableDays = Math.max(
      Math.max(downloadedBytesRecords.length, sentBytesRecords.length) - 1,
      0,
    );

    setSelectedDayKey(fallbackDay);
    setRangeToKey(fallbackDay);
    setRangeFromKey(shiftDateKey(fallbackDay, Math.min(availableDays, 6)) ?? fallbackDay);
  }, [downloadedBytesRecords.length, latestDateKey, sentBytesRecords.length]);

  const availableRecordsCount = Math.min(
    Math.max(downloadedBytesRecords.length, sentBytesRecords.length),
    90,
  );

  const chartStats = useMemo(() => {
    if (!latestDateKey || availableRecordsCount === 0) {
      return [];
    }

    return Array.from({ length: availableRecordsCount }, (_, index) => {
      const dateKey = shiftDateKey(latestDateKey, index);

      return {
        date: dateKey,
        totalSentBytes: toNumber(sentBytesRecords[index]),
        totalReceivedBytes: toNumber(downloadedBytesRecords[index]),
      };
    });
  }, [availableRecordsCount, downloadedBytesRecords, latestDateKey, sentBytesRecords]);

  const selectedDayStats = useMemo(() => {
    const selectedDayOffset = diffDaysFromLatest(latestDateKey, selectedDayKey);

    if (selectedDayOffset == null || selectedDayOffset < 0 || selectedDayOffset >= availableRecordsCount) {
      return {
        downloaded: 0,
        sent: 0,
        hasData: false,
      };
    }

    return {
      downloaded: toNumber(downloadedBytesRecords[selectedDayOffset]),
      sent: toNumber(sentBytesRecords[selectedDayOffset]),
      hasData: true,
    };
  }, [availableRecordsCount, downloadedBytesRecords, latestDateKey, selectedDayKey, sentBytesRecords]);

  const rangeStats = useMemo(() => {
    const fromOffset = diffDaysFromLatest(latestDateKey, rangeFromKey);
    const toOffset = diffDaysFromLatest(latestDateKey, rangeToKey);

    if (fromOffset == null || toOffset == null) {
      return {
        downloaded: 0,
        sent: 0,
        hasData: false,
      };
    }

    const startIndex = Math.max(0, Math.min(fromOffset, toOffset));
    const endIndex = Math.min(availableRecordsCount - 1, Math.max(fromOffset, toOffset));

    if (availableRecordsCount === 0 || startIndex > endIndex) {
      return {
        downloaded: 0,
        sent: 0,
        hasData: false,
      };
    }

    let downloaded = 0;
    let sent = 0;

    for (let index = startIndex; index <= endIndex; index += 1) {
      downloaded += toNumber(downloadedBytesRecords[index]);
      sent += toNumber(sentBytesRecords[index]);
    }

    return {
      downloaded,
      sent,
      hasData: true,
    };
  }, [availableRecordsCount, downloadedBytesRecords, latestDateKey, rangeFromKey, rangeToKey, sentBytesRecords]);

  function formatBytes(bytes) {
    if (!+bytes) return '0 Bytes';

    return `${parseFloat((bytes / 1073741824).toFixed(4))} GB`;
  }

  const minSelectableDay = oldestAvailableDateKey ? dayjs(oldestAvailableDateKey) : null;
  const maxSelectableDay = latestDateKey ? dayjs(latestDateKey) : null;

  const tabLabelSx = {
    textTransform: 'none',
    fontWeight: 600,
  };

  return (
    <Card
      sx={{
        mt: 3,
        backgroundColor: isDarkTheme ? 'background.paper' : 'common.white',
      }}
    >
      <CardContent>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h6">Internet usage statistics</Typography>
            <Typography variant="body2" color="text.secondary">
              {latestDateKey
                ? `Latest saved day: ${formatDateLabel(latestDateKey)}`
                : 'No internet usage records available yet.'}
            </Typography>
          </Box>

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en">
            <TabContext value={currentTab}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList onChange={(event, nextTab) => setCurrentTab(nextTab)} aria-label="internet usage tabs" variant="scrollable" scrollButtons="auto">
                  <Tab label="Per day" value="per-day" sx={tabLabelSx} />
                  <Tab label="Chart" value="chart" sx={tabLabelSx} />
                  <Tab label="Range" value="range" sx={tabLabelSx} />
                </TabList>
              </Box>

              <TabPanel value="per-day" sx={{ px: 0, py: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} lg={4}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        borderColor: isDarkTheme ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                      }}
                    >
                      <CardContent>
                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                          Pick a day
                        </Typography>
                        <DateCalendar
                          value={selectedDayKey ? dayjs(selectedDayKey) : null}
                          onChange={(newValue) => setSelectedDayKey(newValue ? newValue.format('YYYY-MM-DD') : null)}
                          minDate={minSelectableDay ?? undefined}
                          maxDate={maxSelectableDay ?? undefined}
                        />
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} lg={8}>
                    <Stack spacing={3}>
                      <Box>
                        <Typography variant="h6">{formatDateLabel(selectedDayKey)}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {selectedDayStats.hasData
                            ? 'Daily usage recorded for the selected day.'
                            : 'No usage record was stored for this day.'}
                        </Typography>
                      </Box>

                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <StatCard
                            icon={<CloudDownloadIcon sx={{ fontSize: 28 }} />}
                            title={t('views.screenDetail.receivedLabel')}
                            value={formatBytes(selectedDayStats.downloaded)}
                            subtitle={selectedDayStats.hasData ? 'Downloaded bytes' : 'No data for the chosen day'}
                            accentColor="#00E396"
                            isDarkTheme={isDarkTheme}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <StatCard
                            icon={<CloudUploadIcon sx={{ fontSize: 28 }} />}
                            title={t('views.screenDetail.sentLabel')}
                            value={formatBytes(selectedDayStats.sent)}
                            subtitle={selectedDayStats.hasData ? 'Sent bytes' : 'No data for the chosen day'}
                            accentColor="#008FFB"
                            isDarkTheme={isDarkTheme}
                          />
                        </Grid>
                      </Grid>
                    </Stack>
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value="chart" sx={{ px: 0, py: 3 }}>
                {chartStats.length > 0 ? (
                  <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 460 }}>
                      <ScreenDataUsageStatsChart screenStats={chartStats} formatBytes={formatBytes} />
                    </Box>
                  </Box>
                ) : (
                  <Card variant="outlined" sx={{ p: 3, backgroundColor: isDarkTheme ? 'rgba(255,255,255,0.03)' : '#fafafa' }}>
                    <Typography variant="h6">No chart data yet</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Once the device starts reporting internet usage, the last 90 days will appear here.
                    </Typography>
                  </Card>
                )}
              </TabPanel>

              <TabPanel value="range" sx={{ px: 0, py: 3 }}>
                <Stack spacing={3}>
                  <Box>
                    <Typography variant="subtitle1">Select a date range</Typography>
                    <Typography variant="body2" color="text.secondary">
                      The totals below cover the selected inclusive range.
                    </Typography>
                  </Box>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
                    <DatePicker
                      label="From"
                      value={rangeFromKey ? dayjs(rangeFromKey) : null}
                      onChange={(newValue) => setRangeFromKey(newValue ? newValue.format('YYYY-MM-DD') : null)}
                      minDate={minSelectableDay ?? undefined}
                      maxDate={maxSelectableDay ?? undefined}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', md: 'block' } }}>
                      to
                    </Typography>
                    <DatePicker
                      label="To"
                      value={rangeToKey ? dayjs(rangeToKey) : null}
                      onChange={(newValue) => setRangeToKey(newValue ? newValue.format('YYYY-MM-DD') : null)}
                      minDate={minSelectableDay ?? undefined}
                      maxDate={maxSelectableDay ?? undefined}
                    />
                  </Stack>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <StatCard
                        icon={<CloudDownloadIcon sx={{ fontSize: 28 }} />}
                        title={t('views.screenDetail.receivedLabel')}
                        value={formatBytes(rangeStats.downloaded)}
                        subtitle={rangeStats.hasData ? `${formatDateLabel(rangeFromKey)} - ${formatDateLabel(rangeToKey)}` : 'No data in the selected range'}
                        accentColor="#00E396"
                        isDarkTheme={isDarkTheme}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <StatCard
                        icon={<CloudUploadIcon sx={{ fontSize: 28 }} />}
                        title={t('views.screenDetail.sentLabel')}
                        value={formatBytes(rangeStats.sent)}
                        subtitle={rangeStats.hasData ? `${formatDateLabel(rangeFromKey)} - ${formatDateLabel(rangeToKey)}` : 'No data in the selected range'}
                        accentColor="#008FFB"
                        isDarkTheme={isDarkTheme}
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </TabPanel>
            </TabContext>
          </LocalizationProvider>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ScreenInternetUsage;
